import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Stack, Typography } from 'investira.react.components';
import SortButton from '../../../components/molecules/SortButton';
import { SearchEmpresas } from '../../../components/organisms';
import {
  readEmpresasFilterQuery,
  removeEmpresasPaginationFromPath,
  serializeEmpresasFilterQuery,
  updateEmpresasFilterQuery
} from '../_shared/empresasFilterQuery';
import { getEmpresasFilterConfig } from '../_shared/empresasFilterRegistry';
import { getEmpresasSetorRouteContext } from '../_shared/empresasSetorRoute';

/**
 * Compõe a busca e o resumo da listagem abaixo do título de Empresas.
 *
 * @param {Object} pProps Propriedades recebidas da listagem paginada.
 * @param {Function} pProps.dataRead Consulta compartilhada com a listagem.
 * @param {boolean} pProps.pageIsLoading Indica se a página está carregando.
 * @param {number} pProps.totalResults Quantidade total de empresas encontradas.
 * @returns {JSX.Element} Busca contextual seguida pela quantidade de resultados.
 */
const EmpresasTerms = memo(pProps => {
  const xLocation = useLocation();
  const xNavigate = useNavigate();
  const { setor: xSetor } = getEmpresasSetorRouteContext(xLocation.pathname);
  const xConfig = getEmpresasFilterConfig();
  const xQuery = readEmpresasFilterQuery(xLocation.search);
  const xSearchParams = { ...xQuery };

  // A busca rápida usa os filtros aplicados, mas mantém pesquisa e ordenação próprias.
  delete xSearchParams.pesquisa;
  delete xSearchParams.sort;

  /** Aplica a ordenação, preserva pesquisa e filtros e retorna à primeira página. */
  function pvChangeSort(pSort) {
    const xNextQuery = updateEmpresasFilterQuery(xQuery, { sort: pSort || null });
    const xSearch = serializeEmpresasFilterQuery(xNextQuery);
    const xPathname = removeEmpresasPaginationFromPath(xLocation.pathname);

    xNavigate(`${xPathname}${xSearch}`);
  }

  return (
    <Stack spacing={2} mb={4}>
      <SearchEmpresas
        dataRead={pProps.dataRead}
        dataReadParams={xSearchParams}
        pageIsLoading={pProps.pageIsLoading}
        setorId={xSetor?.setor_id}
      />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}>
        <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 600 }}>
          {pProps.totalResults} resultados
        </Typography>
        <Stack sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 280 } }}>
          <SortButton
            id="empresas-sort"
            ariaLabel="Ordenar empresas"
            value={xQuery.sort || ''}
            options={xConfig.sortOptions}
            groups={xConfig.sortGroups}
            onChange={pvChangeSort}
          />
        </Stack>
      </Stack>
    </Stack>
  );
});

EmpresasTerms.propTypes = {
  dataRead: PropTypes.func.isRequired,
  pageIsLoading: PropTypes.bool,
  totalResults: PropTypes.number
};

EmpresasTerms.defaultProps = {
  pageIsLoading: false,
  totalResults: 0
};

EmpresasTerms.displayName = 'EmpresasTerms';

export default EmpresasTerms;
